/*
 * lp-upsell-standalone.js
 * ==================================================================
 * Behaviour for the standalone inline upsell section (sections/lp-upsell.liquid), which renders
 * the shared upsell card (snippets/lp-upsell-card.liquid) OUTSIDE the checkout dialog.
 *
 * No virtual cart: each card's Add posts that one product to /cart/add.js (authored price; the
 * bundle discount is a multi-item Hy mechanic and doesn't apply to a single direct add) and then
 * goes to /checkout. The card's own loading state (disable + spinner) is reused via the same
 * data-upsell-add hook and .is-busy class.
 *
 * One product per section; a section may carry several `upsell-detail` sets (packshot + copy) that
 * cross-fade on a timer (loops) — handled per host below.
 *
 * Multi-instance safe: each section emits this <script>, so with several stacked sections the file
 * executes more than once. An idempotent guard makes only the first run bind (it already selects
 * ALL hosts), so nothing double-binds.
 *
 * Stacking sequencer (only when >1 section is on the page — see `stacked` below): each section is
 * pinned fixed full-viewport (CSS `--stacked` class); only the current one is un-hidden.
 *   - `show_skip_button` (editor setting, sections/lp-upsell.liquid) — whether Skip + ✕ render at all
 *     vs a single Buy Now button. Purely visual; independent of a section's position.
 *   - "Is this the LAST section" (whether Add/Skip/✕ go to checkout/cart vs advance) is NOT an editor
 *     setting — Liquid can't see sibling sections, so it's worked out here from DOM order instead
 *     (isFinal = last element of `roots`). Always true for a lone section (index 0 of 0), so a page
 *     with only one section behaves exactly as before — zero change for other templates.
 * On a non-final section, Skip/✕ always just advanceStack(). On the FINAL section (if it shows
 * Skip/✕ at all), they instead behave like "proceed without adding this one": go to checkout/cart
 * if the cart already has something in it, otherwise disabled (see cartHasItems/syncFinalSkip below)
 * — there's no point sending a shopper to an empty checkout. Add always adds first regardless.
 *
 * Cart wipe on entry (checkout pathway only): when window.ZoevaLp.redirectTarget is 'checkout',
 * this page is meant to be an isolated single-item purchase, so any stale items already in the
 * cart (a prior visit, another tab, another LP) are cleared on a fresh load before anything else
 * runs — see wipeCartIfCheckout(). The 'cart' pathway is unchanged/unaffected — nothing is wiped.
 */
(function () {
  if (window.__lpUpsellInit) return; // stacked sections re-run this file; bind once
  window.__lpUpsellInit = true;

  const roots = document.querySelectorAll('[data-lp-upsell-standalone]');
  if (!roots.length) return;

  const root = (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) || '/';

  // ---- Hy discount eligibility ---------------------------------------------------------------
  // The live discount global injected by the Hy "zoeva-discounts" app embed (same pattern as
  // assets/landing-page.js's lpDiscountApi()). Read live (not cached) so this stays correct
  // even if the embed's script finishes loading after this file runs.
  function lpDiscountApi() {
    return (
      window.Hy && window.Hy.ZoevaDiscounts && window.Hy.ZoevaDiscounts.landingPageExclusiveDiscount
    );
  }

  // The raw discount-rule config Hy publishes on the page (same script-element lookup as
  // buildMilestones() in landing-page.js). Null on any failure (no Hy, no element, bad JSON) —
  // callers treat that as "not eligible", never as "eligible by default".
  function readDiscountConfig() {
    const api = lpDiscountApi();
    const el = api && api.functionId && document.getElementById(api.functionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // ---- Sequencer setup ----------------------------------------------------------------------
  const slides = Array.from(roots, (host) => host.closest('.lp-upsell-section'));
  const stacked = slides.length > 1;
  let currentIndex = 0;
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Whether the real cart already has anything in it (any product, not just this section's) — used
  // ONLY to gate the final section's Skip/✕ (see the header comment). Starts pessimistic (false)
  // until refreshInCart()'s fetch resolves, so a fast click can't slip through to an empty checkout.
  let cartHasItems = false;
  let finalSlideEl = null; // the section whose Skip/✕ need the cartHasItems gate (set in the roots loop below)

  if (stacked) {
    document.documentElement.classList.add('lp-upsell-stacked');
    // One shared, constant-coloured background behind the whole stack (sampled from the first
    // section — all sections author the same colour) — so stepping through the stack reads as
    // "the card moves", not a scene/background change. Each section wrapper itself goes transparent
    // (see CSS): with a shared opaque bg, only the CARD needs to be shown/hidden/animated below.
    const stackBg = document.createElement('div');
    stackBg.className = 'lp-upsell-stack-bg';
    stackBg.setAttribute('aria-hidden', 'true');
    stackBg.style.background = slides[0].style.getPropertyValue('--ft-bg') || '#e6e6e6';
    document.body.insertBefore(stackBg, document.body.firstChild);
    slides.forEach((slide, i) => {
      slide.classList.add('lp-upsell-section--stacked');
      if (i !== 0) slide.hidden = true; // only the first section starts visible
    });
  }

  // The current section's video only starts fetching when it plays (see the per-host `preload="none"`
  // setup below) — bandwidth-conscious, since a shopper may never scroll to/reach it. But once a
  // section IS current, the NEXT one is about to be needed, so warm just that one video ahead of time
  // (idle-scheduled, so it doesn't compete with the current video's own fetch) — advancing then plays
  // instantly instead of starting from zero. Never warms more than one video ahead of the shopper.
  function scheduleNextVideoPreload(index) {
    if (reduceMotion) return; // that video will only ever show its poster — preloading it wastes bandwidth
    if (navigator.connection && navigator.connection.saveData) return; // respect Data Saver
    const next = index + 1;
    if (next >= roots.length) return;
    const video = roots[next].querySelector('[data-upsell-video]');
    if (!video || video._preloadScheduled) return;
    video._preloadScheduled = true;

    const warm = () => {
      video.preload = 'auto';
      video.load(); // re-evaluate now that preload is "auto" — preload="none" already blocked any fetch
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 2000 });
    } else {
      setTimeout(warm, 300);
    }
  }
  scheduleNextVideoPreload(currentIndex); // warm the 2nd section while the 1st is showing

  function focusPrimary(slide) {
    const cta = slide.querySelector('[data-upsell-add], [data-upsell-no]');
    if (cta && cta.focus) cta.focus({ preventScroll: true });
  }

  // Keep the final section's Skip/✕ (if shown) in sync with whether the cart has anything in it —
  // called after the initial cart fetch and after every successful add, from any section.
  function syncFinalSkip() {
    if (!finalSlideEl) return;
    ['[data-upsell-no]', '[data-upsell-close]'].forEach((sel) => {
      const btn = finalSlideEl.querySelector(sel);
      if (!btn) return;
      if (cartHasItems) btn.removeAttribute('disabled');
      else btn.setAttribute('disabled', '');
    });
  }

  // Advance from the current section to the next one. Only called from a non-final section's
  // Skip/Add, so `stacked` is always true here; the bounds check is just a safety net. Only the
  // CARD (roots[i]) animates — the section wrapper (slides[i]) is just un-hidden/hidden instantly,
  // since it's now transparent (the shared stackBg behind it never changes).
  function advanceStack() {
    const next = currentIndex + 1;
    if (next >= slides.length) return;
    const outgoingSlide = slides[currentIndex];
    const incomingSlide = slides[next];
    const outgoingCard = roots[currentIndex];
    const incomingCard = roots[next];
    currentIndex = next;
    scheduleNextVideoPreload(currentIndex); // warm the section AFTER this one, now that it's current

    outgoingCard.style.pointerEvents = 'none'; // inert while it animates away
    incomingSlide.hidden = false;
    // The detail cross-fade reserves its height by measuring offsetHeight at page load, when a
    // not-yet-current card is inside a `hidden` slide (0-height) — re-measure now it's laid out.
    if (incomingCard._reserveDetailHeight) incomingCard._reserveDetailHeight();

    if (reduceMotion) {
      outgoingSlide.hidden = true;
      outgoingCard.style.pointerEvents = '';
      focusPrimary(incomingSlide);
      return;
    }

    const DUR = 520;
    const EASE = 'cubic-bezier(.4,0,.2,1)';
    // Fold out to the left / slide in from the right — transform + opacity only, so this stays on
    // the compositor thread (no layout or paint) for a smooth handoff on lower-end mobile. Percentages
    // are relative to the CARD's own width, not the viewport, since only the card moves now.
    outgoingCard.animate(
      [
        { transform: 'translateX(0) scale(1)', opacity: 1 },
        { transform: 'translateX(-120%) scale(.92)', opacity: 0 },
      ],
      { duration: DUR, easing: EASE, fill: 'forwards' }
    );
    const incomingAnim = incomingCard.animate(
      [
        { transform: 'translateX(120%) scale(.96)', opacity: 0 },
        { transform: 'translateX(0) scale(1)', opacity: 1 },
      ],
      { duration: DUR, easing: EASE, fill: 'forwards' }
    );
    incomingAnim.onfinish = () => {
      outgoingSlide.hidden = true;
      outgoingCard.style.pointerEvents = '';
      // Drop the fill:'forwards' overrides now that `hidden`/the resting CSS state takes over —
      // matches the project's "animations only while visible" rule (nothing keeps animating, or
      // holds a stale inline style, once a card is out of view).
      outgoingCard.getAnimations().forEach((a) => a.cancel());
      incomingCard.getAnimations().forEach((a) => a.cancel());
      focusPrimary(incomingSlide);
    };
  }

  // Post-add destination. Set page-wide by lp-page-settings via window.ZoevaLp.redirectTarget
  // ('checkout' default | 'cart'). Only this section reads it, so it affects the LP Upsell add only.
  const redirectTarget = window.ZoevaLp && window.ZoevaLp.redirectTarget === 'cart' ? 'cart' : 'checkout';
  function destUrl() {
    return root + redirectTarget;
  }

  // Checkout pathway only: wipe the cart once on a fresh entry, before it's ever read, so this
  // section's add is the only thing that reaches checkout. Not called from the pageshow/bfcache
  // handler below — that path is "shopper hit Back after adding", and wiping there would silently
  // delete what they just added. The 'cart' pathway keeps today's behaviour (never wiped).
  function wipeCartIfCheckout() {
    if (redirectTarget !== 'checkout') return Promise.resolve();
    return fetch(root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).catch(() => {});
  }

  // Fetch the cart and flag, per host, whether its product's variant is already in it — so a
  // repeat BUY NOW doesn't add a duplicate line / bump the quantity (it goes straight to checkout).
  function refreshInCart() {
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        const items = cart.items || [];
        cartHasItems = items.length > 0;
        syncFinalSkip();
        roots.forEach((host) => {
          const vid = Number(host.getAttribute('data-variant-id'));
          host._inCart = items.some((it) => it.variant_id === vid || it.id === vid);
        });
      })
      .catch(() => {});
  }

  roots.forEach((host, i) => {
    // Video: muted autoplay while in view (bandwidth-friendly). Reduced motion → poster only.
    const video = host.querySelector('[data-upsell-video]');
    if (video) {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduce) {
        video.muted = true;
        const play = () => {
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        };
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            (entries) => {
              entries.forEach((en) => {
                if (en.isIntersecting) play();
                else video.pause();
              });
            },
            { threshold: 0.25 }
          ).observe(video);
        } else {
          play();
        }
      }
    }

    // Review carousel: clone the first item for a seamless loop, advance one every 2s. Runs only
    // while the card is in view; reduced-motion / a single review → static. (Mirrors the dialog's
    // carousel in landing-page.js; a shared module is a future cleanup.)
    const track = host.querySelector('[data-upsell-rv-track]');
    if (track) {
      const reduceRv = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const rvCount = track.querySelectorAll('.lp-upsell__rv-item').length;
      if (!reduceRv && rvCount > 1) {
        track.appendChild(track.querySelector('.lp-upsell__rv-item').cloneNode(true)); // seamless tail
        const H = 48; // one review row height (mirrors the section CSS override, tightened to the design)
        let idx = 0;
        let rvTimer = null;
        const advance = () => {
          idx += 1;
          track.style.transition = 'transform .5s cubic-bezier(.4,0,.2,1)';
          track.style.transform = 'translateY(-' + idx * H + 'px)';
          if (idx === rvCount) {
            setTimeout(() => {
              track.style.transition = 'none';
              track.style.transform = 'translateY(0)';
              idx = 0;
              void track.offsetWidth; // flush the reset before the next step
            }, 520);
          }
        };
        const startRv = () => { if (!rvTimer) rvTimer = setInterval(advance, 2000); };
        const stopRv = () => { if (rvTimer) { clearInterval(rvTimer); rvTimer = null; } };
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            (entries) => entries.forEach((en) => (en.isIntersecting ? startRv() : stopRv())),
            { threshold: 0.1 }
          ).observe(track);
        } else {
          startRv();
        }
      }
    }

    // Detail cross-fade: cycle the packshot+copy sets on a timer (loops), only while in view.
    // BASIC / easily swappable — the design files will refine timing + transition. Reduced motion →
    // the first set stays shown (no auto-advance).
    const details = host.querySelector('[data-upsell-details]');
    if (details) {
      const sets = details.querySelectorAll('[data-upsell-detail]');
      if (sets.length > 1) {
        // Reserve the slot height BEFORE the sets go absolute (measured in flow = natural height),
        // so the overlaid, absolutely-positioned sets don't collapse the container to zero.
        const reserve = () => {
          let m = 0;
          sets.forEach((s) => { m = Math.max(m, s.offsetHeight); });
          if (m) details.style.height = m + 'px';
        };
        host._reserveDetailHeight = reserve; // re-measured by advanceStack() when a hidden slide is revealed
        reserve();
        details.setAttribute('data-multi', ''); // CSS now stacks + fades the sets
        // Packshot images can load after this runs and change a set's height → re-reserve.
        window.addEventListener('load', reserve);
        window.addEventListener('resize', reserve);

        const reduceD = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduceD) {
          let di = 0;
          const interval = (parseFloat(host.getAttribute('data-detail-interval')) || 4) * 1000;
          const FADE = 700; // ms — matches the CSS opacity transition; the current set fades fully OUT
          let dTimer = null;
          let fadeTimer = null;
          const stepD = () => {
            // Sequential fade: fade the current set OUT, then fade the next one IN once it's gone
            // (the video shows through between) — a clearer in/out than a simultaneous cross-dissolve.
            sets[di].removeAttribute('data-active');
            di = (di + 1) % sets.length;
            const next = di;
            fadeTimer = setTimeout(() => sets[next].setAttribute('data-active', ''), FADE);
          };
          const startD = () => { if (!dTimer) dTimer = setInterval(stepD, interval); };
          const stopD = () => {
            if (dTimer) { clearInterval(dTimer); dTimer = null; }
            if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
          };
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(
              (entries) => entries.forEach((en) => (en.isIntersecting ? startD() : stopD())),
              { threshold: 0.25 }
            ).observe(details);
          } else {
            startD();
          }
        }
      }
    }

    // Skip/✕ (only present when the section's "Show Skip button" setting is on — hide_dismiss is
    // false for it). Behaviour depends on whether this is the LAST section in DOM order, independent
    // of whether they're shown at all: non-final → always advance. Final → cart-aware "proceed
    // without adding" (see syncFinalSkip).
    const slide = host.closest('.lp-upsell-section');
    const isFinal = i === roots.length - 1;
    if (isFinal) finalSlideEl = slide;
    ['[data-upsell-no]', '[data-upsell-close]'].forEach((sel) => {
      const btn = host.querySelector(sel);
      if (!btn) return;
      if (isFinal) {
        btn.setAttribute('disabled', ''); // pessimistic until syncFinalSkip() confirms the cart state
        btn.addEventListener('click', () => {
          if (btn.hasAttribute('disabled')) return; // no items yet — nothing to check out
          window.location.href = destUrl();
        });
      } else {
        btn.addEventListener('click', () => advanceStack());
      }
    });

    const add = host.querySelector('[data-upsell-add]');
    const variantId = Number(host.getAttribute('data-variant-id'));
    if (!add || !variantId) return;

    add.addEventListener('click', () => {
      if (add.hasAttribute('disabled')) return;
      // Reuse the card's loading treatment: disable + spin the acting button.
      add.setAttribute('disabled', '');
      add.classList.add('is-busy');

      // Final section: unchanged — add (if needed) then leave the LP for checkout/cart. Non-final:
      // add to the real cart, then hand off to the next section instead of navigating away.
      const proceed = () => {
        if (isFinal) {
          window.location.href = destUrl();
          return;
        }
        add.removeAttribute('disabled');
        add.classList.remove('is-busy');
        advanceStack();
      };

      // Already in the cart → don't add again (that would bump the quantity).
      if (host._inCart) {
        proceed();
        return;
      }

      // Only tag the line _lp_exclusive (→ eligible for the Hy checkout discount) when this
      // product is genuinely configured for it — never unconditionally (see lpDiscountApi above).
      const productId = host.getAttribute('data-product-id');
      const eligible =
        window.LpPricing &&
        window.LpPricing.isProductExclusiveEligible(readDiscountConfig(), productId);
      const item = { id: variantId, quantity: 1 };
      if (eligible) item.properties = { _lp_exclusive: 'true' };

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('add failed');
          host._inCart = true;
          cartHasItems = true; // this add just put something in the cart — unlock the final section's Skip/✕
          syncFinalSkip();
          proceed();
        })
        .catch(() => {
          // Re-enable so the shopper can retry rather than being stuck on a spinning button.
          add.removeAttribute('disabled');
          add.classList.remove('is-busy');
        });
    });
  });

  wipeCartIfCheckout().then(refreshInCart); // on load (wipes first when the pathway is checkout)

  // Browser Back from /checkout restores this page from the bfcache with the button frozen in its
  // spinning/disabled state. Reset it so it's clickable again, and re-check the cart (the item may
  // now be in it — added, then Back). (The dialog gets the reset for free via lp-cart's pageshow
  // reload; the standalone has no cart to reload, so just clear the button + re-read the cart.)
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return; // fresh loads already start clean + will refreshInCart() above
    roots.forEach((host) => {
      const add = host.querySelector('[data-upsell-add]');
      if (add) {
        add.removeAttribute('disabled');
        add.classList.remove('is-busy');
      }
    });
    refreshInCart();
  });
})();
